import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { without } from '@/components/catalog/form-utils';
import { ApiError, api } from '@/lib/api';
import { isValidInclusionQuantity, renderInclusion } from '@/lib/inclusions';
import {
  LINE_CITIES_MAX,
  citiesLostByMovingProject,
  lineRefusalOf,
  type LineRefusal,
} from '@/lib/line-project';
import { citiesOfLineProject, useCycleScopeIds, useLineProject } from '@/lib/use-line-project';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectInCycleScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import {
  DISCOUNT_MODE_GROUP_LABEL,
  DISCOUNT_MODE_OPTIONS,
  discountModeOf,
  type DiscountMode,
} from '@/lib/discount-mode';
import { recommendedDiscountSeed } from '@/lib/line-presets';
/*
 * Project pricing (owner 2026-10-27): the price an untouched line inherits is
 * `computeLinePrice` (project price entry, else the legacy catalogue rule).
 */
import { computeLinePrice, tierOptionsFor, type ComputedLinePrice } from '@/lib/line-pricing';
import type { ProjectPriceEntry } from '@/lib/project-pricing';
import { useProjectPrices } from '@/lib/use-project-prices';
import {
  defaultQuantityFor,
  perUnitDiscountTotal,
  quantityStepError,
  type ProductUnitFields,
} from '@/lib/product-units';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Notice } from '@/components/ui/Notice';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { useReferenceOptions } from '@/lib/use-reference-options';
import { cityLabel } from '@/lib/use-project-options';
import type {
  CampaignInclusion,
  DealProduct,
  DealProductCreateInput,
  DealProductUpdateInput,
  Intake,
  LineInclusionPayload,
} from '@/lib/types';
import { Button, Combobox, Field, Input, Modal, MultiCombobox, Select } from '@/components/ui';
import type { ComboboxOption, MultiComboboxOption } from '@/components/ui';

/**
 * The parts of an existing line that decide whether another line may be
 * added — structural, so `DealDetailPage` passes its `DealProduct[]`
 * straight in.
 *
 * ⚠️ Excludes by (product, project), never product alone: `UNIQUE (deal_id,
 * product_id, project_key)` (migration 067) makes the same product a legal
 * second line under a different project — a festival really is sold twice
 * for two periods. Excluding by product id alone hides a line the deal is
 * meant to allow.
 *
 * Two lines for one product with NO project are still refused — `null`
 * compares equal to `null`, matching the server's `COALESCE(project_id, 0)`.
 */
export interface DealLineIdentity {
  id: number;
  product: { id: number };
  project: { id: number } | null;
}

export interface LineItemFormModalProps {
  open: boolean;
  onClose: () => void;
  dealId: number;
  /** Omitted = add mode. Provided = edit mode. */
  line?: DealProduct;
  /** Every line already on this deal, the one being edited included. */
  existingLines: DealLineIdentity[];
  onSaved: (line: DealProduct) => void;
  /**
   * `deal.currency` — picks the project price entry. Omitted = read off the
   * deal (`GET /deals/:id`); `null` = the deal records none (catalogue rule).
   */
  currency?: string | null;
}

/**
 * ⛔ **TWO MONEY PATTERNS, AND MERGING THEM AGAIN IS A SURCHARGE.**
 *
 * ⚠️ *SUPERSEDED, and the condition it named has now FIRED: "The single use
 * below is the price and nothing else — if this file ever validates
 * `discountAmount`, that field stays UNSIGNED and needs its own pattern."* This
 * form gained an amount discount on 2026-09-12, so it validates `discountAmount`
 * now and {@link DISCOUNT_AMOUNT_PATTERN} below is that pattern.
 *
 * Migration 171 lets a line PRICE be negative (a DEDUCTION LINE), while
 * `discountAmount` stays non-negative under `ck_*_discount_amount_sign`. A
 * negative in the first box is a credit; a negative in the second is a
 * **surcharge arriving through the discount field** — a wrong number on a
 * document, refused by the database only as errno 4025, which is a 422 naming
 * no field at all and which nobody can bind to the control they typed in.
 *
 * `DealLineItemsTable` carries the same pair for the same columns, and the
 * backend pins the behaviour in `tests/line-negative-unit-price.test.ts`.
 * **They differ by one character and must never converge.**
 */

/** `unitPrice`: `^-?\d{1,10}(\.\d{1,2})?$` — SIGNED since migration 171. */
const PRICE_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;
/** `discountAmount`: UNSIGNED, matching the CHECK constraint on the line table. */
const DISCOUNT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;

/**
 * True when the product is sold PER UNIT (a `unitLabel` or a `quantityStep`),
 * so its recommended discount is a per-unit figure. Flag columns only.
 */
function soldPerUnit(units: ProductUnitFields | undefined): boolean {
  if (quantityStepOf(units) !== undefined) return true;
  return (units?.unitLabel?.trim() ?? '') !== '';
}

/** The product's step as an input attribute, or `undefined` when it has none. */
function quantityStepOf(units: ProductUnitFields | undefined): number | undefined {
  const step = units?.quantityStep;
  return typeof step === 'number' && Number.isInteger(step) && step >= 1 ? step : undefined;
}
/** `discount`: a PERCENTAGE, `^\d{1,3}(\.\d{1,2})?$`, additionally bounded at 100. */
const DISCOUNT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/** The server's cap. 51 entries is a 422 naming the key. */
const INCLUSIONS_MAX = 50;

/**
 * The scalar fields, which are diffed key by key against the loaded record.
 *
 * The three SET-shaped fields — cities, inclusions and the project-picker
 * narrowing — are held separately, because a `Record<string, string>` diff
 * cannot express "replace the whole set" and the narrowing is not sent at all.
 *
 * ⚠️ **`discountAmount` is held separately too, and for a different reason: it
 * is `string | null`, and the `null` is the MODE.** A `Record<string, string>`
 * cannot tell "the amount box is empty" from "this line discounts by
 * percentage", and those are the two mutually exclusive columns — so folding it
 * in here would lose the one distinction the pair turns on. `discount` below
 * stays, because it really is a string.
 */
interface FormValues {
  productId: string;
  /** '' = no project on this line, which is a real and common state. */
  projectId: string;
  quantity: string;
  unitPrice: string;
  /** A PERCENTAGE. Inert while the line is in amount mode — see {@link discountWriteOf}. */
  discount: string;
  sortOrder: string;
}

/**
 * What the draft's discount MEANS on the wire — the pair, resolved once.
 *
 * ⚠️ **THE TWO KEYS TRAVEL TOGETHER, ALWAYS.** `discount` and `discountAmount`
 * are mutually exclusive columns and **sending one while the other stands IS the
 * 422** (`ck_*_discount_amount_exclusive`, naming `discountAmount` — a field this
 * form must therefore be able to mark). Read structurally rather than case by
 * case: whatever this returns, one half is inert by construction, so a row
 * stating two live discounts cannot be built from it.
 *
 * ⚠️ **An emptied AMOUNT box is "no discount", not a fall back to the
 * percentage.** The percentage control is not on screen in that mode, so leaving
 * a stored percentage standing would show an empty discount box over a line that
 * is still discounted.
 *
 * ⚠️ **Nothing here converts one figure into the other**, and nothing may. That
 * conversion was lossy by POUNDS rather than pence and was deleted when
 * `discount_amount` shipped.
 */
function discountWriteOf(
  discount: string,
  amount: string | null,
): { discount: string; discountAmount: string | null } {
  if (amount === null) {
    const percentage = discount.trim();
    return { discount: percentage === '' ? '0' : percentage, discountAmount: null };
  }
  const exact = amount.trim();
  return { discount: '0', discountAmount: exact === '' ? null : exact };
}

/**
 * The three controls that NARROW the project list, and are never sent — not
 * fields of the line. Putting them in `FormValues` would put them in
 * `changedKeys` and from there into a `.strict()` PATCH, a 422 naming a key
 * the user thinks of as a search box.
 */
interface Narrowing {
  regionId: string;
  cycleId: string;
  intakeId: string;
}

/**
 * One chosen inclusion, held in the order it will be written.
 *
 * `quantity` is a string because it is typed into an `<input>`; it becomes a
 * real JSON number in the payload. The array index becomes `sortOrder`, so the
 * order on screen is the order printed on the document.
 */
interface SelectedInclusion {
  inclusionId: number;
  quantity: string;
}

function toFormValues(line?: DealProduct): FormValues {
  return {
    productId: line ? String(line.product.id) : '',
    projectId: line?.project ? String(line.project.id) : '',
    quantity: line ? String(line.quantity) : '1',
    unitPrice: line?.unitPrice ?? '',
    discount: line?.discount ?? '',
    sortOrder: line ? String(line.sortOrder) : '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * ⚠️ These are the keys the form SENDS, not the ones it labels — a path can
 * be non-empty and still bind to nothing, which looks bindable but routes to
 * an input that doesn't exist (the retired singular `cityId` is the live
 * example). Derived from `toFormValues()` plus the keys held outside it. No
 * `taxRate` — a DEAL line has none.
 *
 * ⚠️ **`discountAmount` is on this list and must stay on it.** It is the field
 * the exclusivity CHECK names, so without it the one refusal the discount pair
 * can produce lands in the banner as an unroutable sentence about a box that is
 * sitting on screen, marked as fine.
 */
const KNOWN_WRITE_KEYS: readonly string[] = [
  ...Object.keys(toFormValues()),
  'discountAmount',
  'priceTierId',
  'cityIds',
  'regionIds',
  'inclusions',
];

/**
 * The line's stored cities, in the order the server sent them.
 *
 * **Not re-sorted.** `LineCity.sortOrder` is a curated per-line order and the
 * array already reflects it; re-sorting by name "to be tidy" changes what the
 * customer reads, and the order is what goes back out in `cityIds`.
 */
function toCityIds(line?: DealProduct): string[] {
  return (line?.cities ?? []).map((city) => String(city.id));
}

/**
 * The line's stored REGIONS of focus, in the order the server sent them.
 *
 * ⚠️ **Nothing to do with `Narrowing.regionId`**, which narrows the project
 * search and is never sent. This one is a field of the line — see the two
 * controls, which say so at both sites.
 */
function toRegionIds(line?: DealProduct): string[] {
  return (line?.regions ?? []).map((region) => String(region.id));
}

function toSelectedInclusions(line?: DealProduct): SelectedInclusion[] {
  // `inclusions` is always present on a line and `[]` when there are none, so
  // the only absence guarded here is the LINE's, in add mode.
  return [...(line === undefined ? [] : line.inclusions)]
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
    .map((inclusion) => ({ inclusionId: inclusion.id, quantity: String(inclusion.quantity) }));
}

/** Order-sensitive: reordering the set is a real change the server will store. */
function inclusionsKey(selected: SelectedInclusion[]): string {
  return selected.map((entry) => `${entry.inclusionId}:${entry.quantity.trim()}`).join('|');
}

/** Order-sensitive for the same reason — `cityIds` is written as a sequence. */
function cityKey(ids: string[]): string {
  return ids.join('|');
}

/**
 * Order-INSENSITIVE, unlike `cityKey`: `regionIds` carries no per-line
 * `sortOrder`, so re-ordering the same set is not a change and must not send a
 * full-replace write that stores nothing new.
 */
function regionKey(ids: string[]): string {
  return [...new Set(ids)].sort().join('|');
}

function toNumberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/** A decimal string's canonical form (`"7000"` = `"7000.00"`), or `null` when it is not one. */
function canonicalDecimal(value: string): string | null {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
  if (!match) return null;
  const [, sign = '', whole = '0', fraction = ''] = match;
  const int = whole.replace(/^0+(?=\d)/, '');
  const frac = fraction.replace(/0+$/, '');
  const zero = /^0$/.test(int) && frac === '';
  return `${zero ? '' : sign}${int}${frac === '' ? '' : `.${frac}`}`;
}

/** Exact, scale-insensitive equality of two decimal strings. */
function sameDecimal(a: string, b: string): boolean {
  const left = canonicalDecimal(a);
  return left !== null && left === canonicalDecimal(b);
}

/** `''`, `0`, `0.00`: a percentage nobody set. */
function isBlankOrZero(value: string): boolean {
  return /^(0+(\.0+)?)?$/.test(value.trim());
}

/** `deal.currency`, as given, else read once off the deal. */
function useDealCurrency(dealId: number, given: string | null | undefined): string | null {
  const [fetched, setFetched] = useState<string | null>(null);
  useEffect(() => {
    if (given !== undefined) return;
    const controller = new AbortController();
    api
      .getDeal(dealId, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setFetched(res.data.currency);
      })
      // Unread = the catalogue rule, which is what an unpriced deal gets anyway.
      .catch(() => {});
    return () => controller.abort();
  }, [dealId, given]);
  return given !== undefined ? given : fetched;
}

export function LineItemFormModal({ open, onClose, line, ...rest }: LineItemFormModalProps) {
  if (!open) return null;
  return <LineItemForm key={line?.id ?? 'new'} onClose={onClose} line={line} {...rest} />;
}

function LineItemForm({
  onClose,
  dealId,
  line,
  existingLines,
  onSaved,
  currency: givenCurrency,
}: Omit<LineItemFormModalProps, 'open'>) {
  const formId = useId();
  const isEdit = line !== undefined;
  const currency = useDealCurrency(dealId, givenCurrency);

  const baseline = useMemo(() => toFormValues(line), [line]);
  /** `null` on a new line, which is percentage mode — the same as an undiscounted one. */
  const baselineDiscountAmount = line?.discountAmount ?? null;
  const baselineCityIds = useMemo(() => toCityIds(line), [line]);
  const baselineRegionIds = useMemo(() => toRegionIds(line), [line]);
  const baselineInclusions = useMemo(() => toSelectedInclusions(line), [line]);

  const [values, setValues] = useState<FormValues>(baseline);
  /**
   * The amount discount, and the MODE: `null` is percentage mode, a string —
   * `''` included — is amount mode. Never a truthiness test.
   */
  const [discountAmount, setDiscountAmount] = useState<string | null>(baselineDiscountAmount);
  /**
   * What {@link recommendedDiscountSeed} last put in the amount box, or `null`
   * if it has put nothing there.
   *
   * ⚠️ **This is the whole re-apply rule, and it is not "is the box empty".** A
   * seed may overwrite a seed and may never overwrite a person, so changing the
   * product re-seeds a figure this form placed and leaves a typed one alone.
   */
  const [seededDiscountAmount, setSeededDiscountAmount] = useState<string | null>(null);
  /** The line's price tier; `null` = the default tier (owner 2026-10-27). */
  const baselinePriceTierId = line?.project ? (line.priceTierId ?? null) : null;
  const [priceTierId, setPriceTierId] = useState<number | null>(baselinePriceTierId);
  /**
   * What this form last put in the Unit price box from the pricing rule, and
   * the rounding discount it put in the amount box. A box still holding the
   * placed figure is untouched and follows the rule; a typed one never does.
   */
  const [placedPrice, setPlacedPrice] = useState<string | null>(null);
  const [placedDiscount, setPlacedDiscount] = useState<string | null>(null);
  const [cityIds, setCityIds] = useState<string[]>(baselineCityIds);
  /**
   * The line's own REGIONS of focus — SAVED, unlike `narrowing.regionId`.
   *
   * Offered only when the product is not sold through projects: a project
   * declares its own regions, so a project-backed line has nothing to say here.
   */
  const [regionIds, setRegionIds] = useState<string[]>(baselineRegionIds);
  const [selected, setSelected] = useState<SelectedInclusion[]>(baselineInclusions);
  const [narrowing, setNarrowing] = useState<Narrowing>({
    regionId: '',
    cycleId: '',
    intakeId: '',
  });

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<LineRefusal | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Names of cities dropped by the last project change, so the form can SAY
   * what it silently removed. Resetting the selection without a word is how a
   * line quietly loses a city between two saves.
   */
  const [droppedCities, setDroppedCities] = useState<string[]>([]);
  /**
   * The user emptied the project box themselves.
   *
   * Without this the "one match selects itself" rule would immediately put the
   * only match straight back, and the control would be impossible to clear.
   */
  const [projectClearedByUser, setProjectClearedByUser] = useState(false);

  const productIdNum = toNumberOrNull(values.productId);
  const projectIdNum = toNumberOrNull(values.projectId);

  const products = useProductSearch(line ?? null);
  const inclusions = useCampaignInclusions(line);
  /** The chosen product's step and unit; absent while it is unread. */
  const selectedUnits =
    productIdNum === null ? undefined : products.unitsById.get(productIdNum);
  const selectedStep = quantityStepOf(selectedUnits);
  const selectedUnitLabel = selectedUnits?.unitLabel?.trim() || null;

  /**
   * The `/regions` master — read ONCE and consumed by TWO controls doing
   * opposite jobs:
   *
   *  - `narrowing.regionId`, a single-select inside `ProjectPicker` that
   *    NARROWS the project search and is never part of any payload;
   *  - `regionIds`, the multiselect below that IS SAVED on the line, offered
   *    only when the product is not sold through projects.
   *
   * ⚠️ One list, two meanings. Sharing the fetch is right; sharing the
   * SELECTION would silently start saving a search box. Do not merge them.
   *
   * `useReferenceOptions` already queries `isActive: true`, so a retired region
   * is never offered — a stored one is pinned back in below.
   */
  const regions = useReferenceOptions('regions');
  const cycles = useCycleOptions();
  const intakes = useIntakeOptions(toNumberOrNull(narrowing.cycleId));

  /**
   * Options for the LINE's region multiselect.
   *
   * A region the line holds that the master no longer offers is pinned back in
   * under the name the READ gave us — reads carry no `isActive` filter, so the
   * line still resolves it, and dropping it would both render a bare id and
   * silently remove it from the set the next save writes. Only CALLED retired
   * once the master has loaded: an in-flight or failed fetch has empty
   * `records`, where every stored region is "absent" and none is retired.
   */
  const lineRegionOptions = useMemo<MultiComboboxOption[]>(() => {
    const options: MultiComboboxOption[] = regions.records.map((row) => ({
      value: String(row.id),
      label: row.name,
    }));
    const loaded = !regions.loading && !regions.failed;
    for (const region of line?.regions ?? []) {
      if (options.some((option) => option.value === String(region.id))) continue;
      options.push({
        value: String(region.id),
        label: region.name,
        ...(loaded ? { hint: 'No longer offered' } : {}),
      });
    }
    return options;
  }, [regions.records, regions.loading, regions.failed, line]);

  /*
   * The project picker. `product` is the only filter the server treats as
   * correctness — a project for another product is a named refusal — so the
   * other three exist purely to make a long list answerable.
   */
  const lineProject = useLineProject(
    {
      productId: productIdNum,
      regionId: toNumberOrNull(narrowing.regionId),
      cycleId: toNumberOrNull(narrowing.cycleId),
      intakeId: toNumberOrNull(narrowing.intakeId),
    },
    true,
  );

  /*
   * Which cycles sit under Current and which under Previous. Owner request,
   * 2026-09-11 -- Current means current AND upcoming, and is the default.
   *
   * A null member means the cycles could not be read and every project stays on
   * offer under that pill; see useCycleScopeIds. Never an empty set.
   */
  const cycleScopeIds = useCycleScopeIds();

  /*
   * ⚠️ Per MOUNT of this modal, and `current` every time. It is a filter over
   * what is offered, never a value, so it is local state and is written
   * nowhere.
   */
  const [projectScope, setProjectScope] = useState<ProjectCycleScope>(
    DEFAULT_PROJECT_CYCLE_SCOPE,
  );

  /*
   * NARROWS WHAT MAY BE CHOSEN, AND NOTHING ELSE. lineProject.projects stays
   * the authority for cities (citiesOfLineProject), for the is-it-retired test
   * and for the injection below, so a line already on a closed cycle keeps its
   * project and its cities. Only these arrays reach the dropdown.
   */
  const projectsByScope = useMemo(() => {
    const byScope = {} as Record<ProjectCycleScope, typeof lineProject.projects>;
    for (const scope of PROJECT_CYCLE_SCOPES) {
      byScope[scope] = lineProject.projects.filter((project) =>
        projectInCycleScope(project, cycleScopeIds[scope]),
      );
    }
    return byScope;
  }, [lineProject.projects, cycleScopeIds]);
  const offeredProjects = projectsByScope[projectScope];

  /**
   * ⚠️ Some products are legitimately not sold through projects at all —
   * `Meet Up`, `Profile Evaluations`, `Miscellaneous`. On those, the project
   * and city controls are HIDDEN rather than disabled: a disabled control
   * invites creating a project that shouldn't exist. Derived from the
   * server rather than a client-side flag.
   *
   * A line that already holds a project is never treated as exempt.
   */
  const projectless = lineProject.productHasNoProjects === true && values.projectId === '';

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  /** `null` is the percentage and a string is the amount — `''` included. */
  const discountMode = discountModeOf(discountAmount);

  const changedKeys = useMemo(() => {
    const keys = Object.keys(baseline) as (keyof FormValues)[];
    return keys.filter((key) => values[key].trim() !== baseline[key].trim());
  }, [values, baseline]);

  const projectChanged = changedKeys.includes('projectId');
  const citiesChanged = cityKey(cityIds) !== cityKey(baselineCityIds);
  const regionsChanged = regionKey(regionIds) !== regionKey(baselineRegionIds);
  const inclusionsChanged = inclusionsKey(selected) !== inclusionsKey(baselineInclusions);
  /**
   * ⚠️ **Read off the RAW draft, not off the resolved pair, and a MODE FLIP
   * counts on its own.** Comparing the resolved pair would read a retyped
   * `10.00` → `10` as no change and answer "Nothing has changed yet." over a box
   * the person had just edited. Over-reporting here costs one key restating a
   * figure; under-reporting is a save that silently does nothing.
   */
  const discountChanged =
    changedKeys.includes('discount') || discountAmount !== baselineDiscountAmount;
  /** The tier this form writes: none without a project. */
  const effectivePriceTierId = projectIdNum === null ? null : priceTierId;
  const tierChanged = effectivePriceTierId !== baselinePriceTierId;
  const isDirty =
    tierChanged ||
    changedKeys.length > 0 ||
    citiesChanged ||
    inclusionsChanged ||
    regionsChanged ||
    discountChanged;

  function setField(key: keyof FormValues, next: string) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
    setRefusal(null);
  }

  /** Every city name this form can put on screen, from any source it holds. */
  const cityNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const project of lineProject.projects) {
      for (const city of project.cities) map.set(city.id, cityLabel(city));
    }
    // The line's own cities last: a stored value must stay renderable even when
    // its project has since dropped it, and reads carry no `isActive` filter.
    for (const city of line?.cities ?? []) map.set(city.id, cityLabel(city));
    return map;
  }, [lineProject.projects, line]);

  const cityNameOf = (id: number) => cityNames.get(id) ?? `City ${id}`;

  /** The cities the LINE's project declares. Never the `/cities` master. */
  const cityChoices = useMemo(
    () => citiesOfLineProject(lineProject.projects, projectIdNum),
    [lineProject.projects, projectIdNum],
  );

  /**
   * The project this line names is not one the picker loaded — retired since the
   * line was saved, or narrowed out by the boxes above. The two are
   * indistinguishable from here, and either way the project's own city list
   * cannot be read: `citiesOfLineProject` finds nothing and answers `[]`.
   *
   * ⚠️ So nothing in this branch may say what the project does or does not
   * run in. An empty `cityChoices` means "not readable", NOT "declares
   * none" — and the line is sitting there with its stored cities selected,
   * exactly the screen on which that sentence would be believed.
   */
  const projectUnresolved =
    projectIdNum !== null &&
    !lineProject.loading &&
    !lineProject.projects.some((project) => project.id === projectIdNum);

  /**
   * ⚠️ Whether "absent from the list" MEANS anything at all.
   *
   * `useLineProject` fetches `?isActive=true`, so absence implies
   * retirement — but only once the fetch has come back well. In flight, or
   * on a failure, `projects` is `[]`, so every stored project is absent —
   * labelling one "no longer offered" would be a confident, false claim.
   *
   * `projectOptions()`/`withStoredOption()` apply the same gate.
   * `projectUnresolved` above asks the broader "can cities be read"; this
   * one asks the narrower "may we say WHY".
   */
  const projectListReadable = !lineProject.loading && lineProject.error === null;

  /**
   * Changing the project.
   *
   * ⚠️ **The city selection is reset to the intersection here, not at submit
   * time.** A PATCH is judged on its POST-MERGE state, so `{projectId}` alone
   * re-validates the STORED cities against the new project and can be refused
   * naming cities that are not on screen. Sending `{projectId, cityIds}`
   * together is the rule; keeping the on-screen set legal at every moment is
   * what makes that rule truthful rather than a silent trim inside the payload
   * builder.
   *
   * ⚠️ **This is the ONLY caller of `citiesLostByMovingProject`, and it is
   * safe only because DEAL lines have no `allCities` flag** (migration 083
   * landed on quote and invoice products only). The day deal lines gain it, a
   * flagged line's `cities` is a RESOLVED set indistinguishable on screen from
   * a stored one, and this call starts naming real cities as "lost" by a move
   * that loses nothing — check `allCities` before trusting this call again.
   */
  function chooseProject(next: string) {
    const nextId = toNumberOrNull(next);
    const allowed = citiesOfLineProject(lineProject.projects, nextId);
    const allowedIds = allowed.map((city) => city.id);
    const allowedSet = new Set(allowedIds);

    const lost = citiesLostByMovingProject(
      cityIds.map((value) => Number(value)),
      allowedIds,
    );

    setValues((prev) => ({ ...prev, projectId: next }));
    setCityIds((prev) => prev.filter((value) => allowedSet.has(Number(value))));
    setDroppedCities(lost.map(cityNameOf));
    setProjectClearedByUser(next === '');
    setClientErrors((prev) => without(without(prev, 'projectId'), 'cityIds'));
    setServerErrors((prev) => without(without(prev, 'projectId'), 'cityIds'));
    setRefusal(null);
  }

  /**
   * Changing the product resets the project and, through it, the cities — and
   * seeds the product's RECOMMENDED DISCOUNT.
   *
   * A project sells exactly one product, so a project left behind by a product
   * change is a guaranteed refusal ("this project sells product 3, but this
   * line sells product 8") on a control the person believes they already set
   * correctly.
   *
   * ⚠️ **THE SEED FIRES HERE AND NOWHERE ELSE, WHICH IS WHY IT IS NOT AN
   * EFFECT.** The owner's rule is what a line starts with *once it names a
   * product*, so it follows a CHOICE. Re-running it on mount would discount an
   * existing, deliberately undiscounted line the moment somebody opened it to
   * fix a quantity — a figure nobody asked for, on a line already agreed.
   *
   * ⛔ **The decision is {@link recommendedDiscountSeed}'s and not this
   * function's**, because it is one copy shared with the three line grids: it
   * refuses to overwrite a live PERCENTAGE (the two columns are mutually
   * exclusive, so seeding over one is either a 422 or a discount somebody
   * negotiated, silently replaced) and refuses to overwrite an amount it did not
   * put there. Do not re-derive either test here.
   *
   * ⚠️ **A product whose row is not in hand seeds NOTHING**, because
   * `recommendedById` has no entry and the recommendation reads `null` — which
   * means *no recommendation*, never `"0.00"`. That is the safe direction and
   * the same rule as every other "absence from a list that never arrived is not
   * evidence" gate in this file: the line's own product is pinned into the
   * options without its catalogue row, so re-picking it changes no figure.
   */
  function chooseProduct(next: string) {
    setField('productId', next);
    /*
     * A new pick starts at the product's step (`defaultQuantityFor`), but only
     * while the box still holds the previous product's starting quantity — a
     * typed quantity survives a change of item.
     */
    const nextUnits = next === '' ? undefined : products.unitsById.get(Number(next));
    const quantity =
      values.quantity.trim() === '' || values.quantity.trim() === defaultQuantityFor(selectedUnits)
        ? defaultQuantityFor(nextUnits)
        : values.quantity;
    setValues((prev) => ({ ...prev, productId: next, projectId: '', quantity }));
    // A tier belongs to a project × product price; a new product starts at the default.
    setPriceTierId(null);
    setCityIds([]);
    setDroppedCities([]);
    setProjectClearedByUser(false);

    seedRecommendedDiscount(next, quantity, seededDiscountAmount);
  }

  /**
   * Seeds the product's recommended discount. A per-unit product (one with a
   * `unitLabel` or `quantityStep`) recommends a PER-UNIT figure, scaled by the
   * quantity with `perUnitDiscountTotal` before it is seeded as the line's
   * amount. Every other product is unchanged.
   */
  function seedRecommendedDiscount(productId: string, quantity: string, seededBefore: string | null) {
    const id = Number(productId);
    const raw = productId === '' ? null : (products.recommendedById.get(id) ?? null);
    const recommended =
      raw !== null && soldPerUnit(products.unitsById.get(id))
        ? perUnitDiscountTotal(raw, quantity.trim())
        : raw;
    const seed = recommendedDiscountSeed(recommended, {
      discountAmount,
      discount: values.discount,
      seededDiscountAmount: seededBefore,
    });
    if (seed === null) return;
    setDiscountAmount(seed);
    setSeededDiscountAmount(seed);
    setClientErrors((prev) => without(without(prev, 'discount'), 'discountAmount'));
    setServerErrors((prev) => without(without(prev, 'discount'), 'discountAmount'));
  }

  /**
   * The Qty box. On a per-unit product an untouched recommended discount
   * follows the quantity: one this form seeded, or a stored one that is
   * exactly the scaled recommendation at the stored quantity. Nothing is seeded
   * onto a line that is not already following it.
   */
  function changeQuantity(next: string) {
    setField('quantity', next);
    if (productIdNum === null || !soldPerUnit(selectedUnits)) return;
    let following = seededDiscountAmount;
    if (following === null && line !== undefined && line.discountAmount !== null) {
      const raw = products.recommendedById.get(productIdNum) ?? null;
      if (
        raw !== null &&
        line.product.id === productIdNum &&
        line.discountAmount === perUnitDiscountTotal(raw, String(line.quantity))
      ) {
        following = line.discountAmount;
      }
    }
    if (following === null) return;
    seedRecommendedDiscount(values.productId, next, following);
  }

  /**
   * Flipping between a percentage and an exact amount.
   *
   * ⚠️ **The mode IS `discountAmount === null`**, so switching is a write to that
   * one piece of state and nothing else. `values.discount` is deliberately left
   * as typed: it is inert in amount mode ({@link discountWriteOf} resolves the
   * pair), and clearing it would throw away a figure somebody may be flipping
   * back to.
   */
  function chooseDiscountMode(next: DiscountMode) {
    setDiscountAmount(next === 'percentage' ? null : '');
    setClientErrors((prev) => without(without(prev, 'discount'), 'discountAmount'));
    setServerErrors((prev) => without(without(prev, 'discount'), 'discountAmount'));
    setRefusal(null);
  }

  function setDiscountAmountValue(next: string) {
    setDiscountAmount(next);
    setClientErrors((prev) => without(prev, 'discountAmount'));
    setServerErrors((prev) => without(prev, 'discountAmount'));
    setRefusal(null);
  }

  /*
   * One match selects itself; several make the person choose; none says so.
   *
   * `d_projects` carries no unique key over product + region + cycle +
   * intake (migration 044) — the same product really can run twice for one
   * period. Ambiguity is surfaced, never resolved: quietly picking one would
   * attach the wrong project's cities to a line, with nothing on the wire to
   * refuse it.
   */
  /*
   * OVER THE OFFERED LIST, NOT THE WHOLE ONE. Auto-select is a choice made on
   * the person's behalf, so it has to obey the rule a manual choice obeys --
   * otherwise a narrowing that resolves to a single CLOSED-cycle project fills
   * the box with a value its own dropdown does not list, and the control
   * renders blank over it.
   *
   * Consequence, stated because it is a real behaviour change: a narrowing
   * matching three projects of which one is still open now selects that one,
   * where before it asked. That is the same rule -- one choosable match selects
   * itself -- applied to the list the person can actually choose from.
   *
   * ⚠️ **PINNED TO `current`, NOT TO THE ACTIVE PILL — 2026-09-11.** Auto-select
   * must reproduce exactly what it did before the pills existed, because
   * `current` is the default and nobody has asked for anything to be filled in
   * on their behalf while they BROWSE. Reading the active pill would mean
   * switching to Previous out of curiosity silently writing a closed-cycle
   * project onto the line, with `projectClearedByUser` then the only way back.
   */
  const soleMatch =
    !lineProject.loading && projectsByScope.current.length === 1
      ? projectsByScope.current[0]
      : undefined;
  if (soleMatch !== undefined && values.projectId === '' && !projectClearedByUser) {
    /*
     * Adjusted during render, not in an effect, so the sole match is never
     * painted as an empty project box for a frame. `projectId === ''` stops
     * it re-running against its own result; `projectClearedByUser` lets the
     * control be emptied at all.
     *
     * Only `projectId` moves here — cities are unreachable while no project
     * is chosen, so there's nothing to reconcile.
     */
    setValues((prev) => ({ ...prev, projectId: String(soleMatch.id) }));
  }

  function setInclusionIds(ids: string[]) {
    setSelected((prev) => {
      const byId = new Map(prev.map((entry) => [String(entry.inclusionId), entry]));
      // Order follows the selection, and the index becomes `sortOrder`, so the
      // order on screen is the order printed on the document.
      return ids.map((id) => byId.get(id) ?? { inclusionId: Number(id), quantity: '1' });
    });
    setClientErrors((prev) => without(prev, 'inclusions'));
    setServerErrors((prev) => without(prev, 'inclusions'));
    /*
     * ⚠️ The refusal goes too, and it did NOT before migration 096.
     *
     * Until an `inclusions` refusal existed, the city and project handlers
     * cleared it and these two did not — harmlessly, because nothing an
     * inclusions edit could do made a project/city refusal stale. Now they can:
     * `INCLUSION_NOT_OFFERED_IN_REGION` arrives on this control, and leaving it
     * standing would keep a banner and a marked chip on screen describing a
     * selection the person has just changed.
     */
    setRefusal(null);
  }

  function setInclusionQuantity(inclusionId: number, quantity: string) {
    setSelected((prev) =>
      prev.map((entry) => (entry.inclusionId === inclusionId ? { ...entry, quantity } : entry)),
    );
    setClientErrors((prev) => without(prev, 'inclusions'));
    setServerErrors((prev) => without(prev, 'inclusions'));
    /*
     * ⚠️ The refusal goes too, and it did NOT before migration 096.
     *
     * Until an `inclusions` refusal existed, the city and project handlers
     * cleared it and these two did not — harmlessly, because nothing an
     * inclusions edit could do made a project/city refusal stale. Now they can:
     * `INCLUSION_NOT_OFFERED_IN_REGION` arrives on this control, and leaving it
     * standing would keep a banner and a marked chip on screen describing a
     * selection the person has just changed.
     */
    setRefusal(null);
  }

  /**
   * The write shape for the inclusions set.
   *
   * ⚠️ Read and write shapes differ under the same key — a read entry is
   * `{id, name, unitSingular, unitPlural, quantity, sortOrder}`; the write
   * entry is `.strict()` and names the id `inclusionId`, so posting the read
   * array back is a 422 on four keys.
   *
   * A write REPLACES the whole set — every entry goes, including untouched
   * ones. `quantity` omitted means 1, so a blank box omits the key. Zero is
   * refused by zod and a CHECK constraint.
   */
  function buildInclusions(): LineInclusionPayload[] {
    return selected.map((entry, index) => {
      const raw = entry.quantity.trim();
      const body: LineInclusionPayload = { inclusionId: entry.inclusionId, sortOrder: index };
      if (raw !== '') body.quantity = Number(raw);
      return body;
    });
  }

  /*
   * `lineTotal` is never sent (generated, `.strict()` schema) and never
   * computed here — the database rounds per line, which is what reports read.
   *
   * ⚠️ A deal line has ONLY `lineTotal` — no `taxRate`, no `lineSubtotal`,
   * no `lineTax`. The quote/invoice three-column money model does not apply
   * here; `computeGeneratedLinePreview` is deliberately not imported.
   *
   * ⚠️ `quantity` and `unitPrice` describe the LINE. Cities only say where
   * it runs and are NEVER multiplied into the money: 24 at 18,000 across
   * Bangalore and Mumbai is 432,000, not 864,000.
   *
   * The acting user is never sent — the server takes it from the bearer token.
   */
  function buildPayload(): DealProductCreateInput | DealProductUpdateInput {
    if (line) {
      const payload: DealProductUpdateInput = {};
      for (const key of changedKeys) {
        const next = values[key].trim();
        if (key === 'productId') payload.productId = Number(next);
        else if (key === 'quantity') payload.quantity = Number(next);
        else if (key === 'unitPrice') payload.unitPrice = next === '' ? null : next;
        else if (key === 'sortOrder' && next !== '') payload.sortOrder = Number(next);
        /*
         * ⚠️ **`discount` is NOT written here, and that is the fix rather than
         * an omission.** It used to be sent alone out of this loop, which on a
         * line carrying a stored `discountAmount` set one live discount while
         * the other stood — the exact 422 the exclusivity CHECK raises, naming
         * `discountAmount`, a field this form had no control for. The pair is
         * written together below.
         */
      }

      /*
       * ⚠️ **THE PAIR, ALWAYS, AND THIS IS THE WHOLE SAFETY ARGUMENT.** Both
       * keys are assigned inside ONE `if`, so there is no path that sets one and
       * leaves the other stored. `discount` is NOT nullable — `null` is a 422 —
       * so it always goes as a string; `discountAmount` is, and its `null` is
       * what says the percentage is the half that counts.
       */
      if (discountChanged) {
        const money = discountWriteOf(values.discount, discountAmount);
        payload.discount = money.discount;
        payload.discountAmount = money.discountAmount;
      }

      /*
       * ⚠️ THE POST-MERGE TRAP. `projectId` alone re-validates the STORED
       * cities against the new project, so it can be refused naming cities
       * the user never sent. The two keys travel together whenever the
       * project moves — `[]` included, the honest way to say "names no
       * cities now".
       */
      if (projectChanged) {
        payload.projectId = projectIdNum;
        payload.cityIds = cityIds.map((value) => Number(value));
      } else if (citiesChanged) {
        // Cities alone are checked against the project the line ALREADY holds,
        // so an ordinary city edit needs no project key.
        payload.cityIds = cityIds.map((value) => Number(value));
      }

      /*
       * ⚠️ THE SAME TRAP, ON THE INCLUSIONS AXIS — migration 096.
       *
       * A product may offer an inclusion only in certain REGIONS, and a line's
       * region comes from its project. So `projectId` alone re-validates the
       * inclusions the line already holds and can answer
       * `422 INCLUSION_NOT_OFFERED_IN_REGION` naming a key the user never
       * touched. Re-sending the set makes the refusal name what is on screen.
       *
       * ⚠️ It re-sends the DRAFT, which on an unedited move is byte-identical to
       * what is stored — never `[]`, which would clear the set with a 200. There
       * is no branch here that can emit an empty array unless the user emptied
       * it, because `buildInclusions()` reads the selection.
       */
      if (inclusionsChanged || projectChanged) payload.inclusions = buildInclusions();

      /*
       * ⚠️ **Sent ONLY when the person actually moved it.** `regionIds` is a
       * full REPLACE whose absent form leaves the stored set alone and whose
       * `[]` CLEARS it, and the control is hidden on any line whose product IS
       * sold through projects — so an unconditional send would wipe a set
       * another surface populated, on a save that only changed the price.
       */
      if (regionsChanged) payload.regionIds = regionIds.map((value) => Number(value));
      if (tierChanged) payload.priceTierId = effectivePriceTierId;
      return payload;
    }

    const payload: DealProductCreateInput = { productId: Number(values.productId) };
    // Real JSON numbers: quantity and sortOrder are not coerced server-side.
    if (values.quantity.trim() !== '') payload.quantity = Number(values.quantity);
    /*
     * An ABSENT unitPrice triggers the snapshot of the catalogue price; an
     * explicit null means "no price" and is honoured as such. A blank box
     * must omit the key, not send null.
     */
    if (values.unitPrice.trim() !== '') payload.unitPrice = values.unitPrice.trim();
    /*
     * ⚠️ **The pair, or neither.** An amount sends `{discountAmount, discount:
     * '0'}` and a percentage sends the percentage alone — the server's default
     * for an omitted `discount` is 0 and `discountAmount` starts null, so a line
     * with no discount says nothing rather than restating two defaults.
     */
    const money = discountWriteOf(values.discount, discountAmount);
    if (money.discountAmount !== null) {
      payload.discount = money.discount;
      payload.discountAmount = money.discountAmount;
    } else if (money.discount !== '0') {
      payload.discount = money.discount;
    }
    if (values.sortOrder.trim() !== '') payload.sortOrder = Number(values.sortOrder);
    if (projectIdNum !== null) payload.projectId = projectIdNum;
    if (effectivePriceTierId !== null) payload.priceTierId = effectivePriceTierId;
    if (cityIds.length > 0) payload.cityIds = cityIds.map((value) => Number(value));
    // Nothing to clear on a create, so an empty set omits the key rather than
    // sending `[]` — identical here, and NOT identical on the PATCH above.
    if (regionIds.length > 0) payload.regionIds = regionIds.map((value) => Number(value));
    if (selected.length > 0) payload.inclusions = buildInclusions();
    return payload;
  }

  /**
   * The pair already on the deal.
   *
   * ⚠️ The taken thing is (product, project), not the product — two lines
   * for the same product under different projects are legal; two under the
   * same project, or with no project at all, are a 409. Checked here only
   * to say so before the round trip.
   */
  const duplicateOfExisting = useMemo(() => {
    if (productIdNum === null) return false;
    return existingLines.some(
      (other) =>
        other.id !== line?.id &&
        other.product.id === productIdNum &&
        (other.project?.id ?? null) === projectIdNum,
    );
  }, [existingLines, line, productIdNum, projectIdNum]);

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};

    if (values.productId === '') found.productId = 'Choose a product.';

    if (duplicateOfExisting) {
      found.productId =
        projectIdNum === null
          ? 'Already on the deal with no project. Edit that line, or give this one a project.'
          : 'Already on the deal under that project. Edit that line, or pick another project.';
    }

    const quantity = values.quantity.trim();
    if (quantity === '') found.quantity = 'Quantity is required.';
    else if (!/^\d+$/.test(quantity)) found.quantity = 'Enter a whole number of 1 or more.';
    else if (Number(quantity) < 1) found.quantity = 'Must be at least 1.';
    else if (!Number.isSafeInteger(Number(quantity))) found.quantity = 'That number is too large.';
    else if (line === undefined || quantity !== String(line.quantity) || values.productId !== String(line.product.id)) {
      /*
       * The product's step (`@/lib/product-units`), said before the server's
       * 422 `QUANTITY_NOT_MULTIPLE`. A stored line nobody moved is legacy and
       * keeps working.
       */
      const stepError = quantityStepError(selectedUnits, quantity);
      if (stepError !== null) found.quantity = stepError;
    }

    const price = values.unitPrice.trim();
    if (price !== '' && !PRICE_PATTERN.test(price)) {
      found.unitPrice = 'Use up to 10 digits and at most 2 decimal places, e.g. 18000.00.';
    }

    /*
     * ⚠️ **Exactly one half of the discount is checked, because exactly one is
     * on screen.** Reporting the other's format would name a box that is not
     * there. Same split as `DealLineItemsTable` on the identical columns.
     */
    if (discountAmount === null) {
      const discount = values.discount.trim();
      if (discount !== '') {
        if (!DISCOUNT_PATTERN.test(discount)) found.discount = 'Use a percentage like 10 or 12.5.';
        else if (Number(discount) > 100) found.discount = 'Cannot be more than 100%.';
      }
    } else {
      const amount = discountAmount.trim();
      /*
       * ⛔ DISCOUNT_AMOUNT_PATTERN, never PRICE_PATTERN — see the block at both
       * declarations. A negative PRICE is a deduction line; a negative DISCOUNT
       * is a surcharge, and the database refuses it only as errno 4025, which
       * arrives as a 422 naming no field at all.
       */
      if (amount !== '' && !DISCOUNT_AMOUNT_PATTERN.test(amount)) {
        found.discountAmount = amount.startsWith('-')
          ? 'A discount cannot be negative. To reduce a line below zero, use a negative price.'
          : 'An amount, up to two decimal places and with no currency symbol.';
      }
    }

    const sortOrder = values.sortOrder.trim();
    if (sortOrder !== '' && !/^\d+$/.test(sortOrder)) {
      found.sortOrder = 'Enter a whole number of 0 or more.';
    }

    // The cap is a denial-of-service bound rather than a business rule — the
    // real ceiling is the project's own list — so it is refused here rather
    // than collecting a 422 for an absurd selection.
    if (cityIds.length > LINE_CITIES_MAX) {
      found.cityIds = `A line can name at most ${LINE_CITIES_MAX} cities.`;
    }

    if (selected.length > INCLUSIONS_MAX) {
      found.inclusions = `A line can carry at most ${INCLUSIONS_MAX} inclusions.`;
    } else {
      const bad = selected.filter((entry) => {
        const raw = entry.quantity.trim();
        // Blank omits the key, and an omitted quantity means 1.
        return raw !== '' && !isValidInclusionQuantity(Number(raw));
      });
      if (bad.length > 0) {
        found.inclusions = 'Every inclusion needs a whole number of 1 or more, or a blank for 1.';
      }
    }

    return found;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    /*
     * React propagates by the REACT tree, not the DOM, so a portalled
     * dialog's submit still reaches a form rendered above it — how a
     * half-filled deal got created once, with `tsc` and lint both green.
     */
    event.stopPropagation();

    const found = validate();
    setClientErrors(found);
    setServerErrors({});
    setRefusal(null);
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) {
      setFormError('Nothing has changed yet.');
      return;
    }

    setSubmitting(true);
    try {
      const result = line
        ? await api.updateDealProduct(dealId, line.id, payload)
        : await api.createDealProduct(dealId, payload as DealProductCreateInput);
      onSaved(result.data);
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * The project/city refusals first. `lineRefusalOf` returns null for
         * "not one of these" only — an unrecognised sentence comes back as
         * `kind: 'unknown'` with the server's own words.
         */
        const refused = lineRefusalOf(caught);
        if (refused !== null) {
          setRefusal(refused);
          // The server's sentence, verbatim: refusals 4, 5 and 6 name ids and
          // product numbers this form cannot derive, so no paraphrase of ours
          // could carry the useful half.
          setServerErrors({ [refused.path]: refused.message });
          setSubmitting(false);
          return;
        }

        if (caught.code === 'QUANTITY_NOT_MULTIPLE') {
          // The product's step, refused by the server: it belongs on the Qty box.
          setServerErrors({ quantity: caught.fieldErrors.quantity ?? caught.message });
        } else if (caught.status === 409) {
          /*
           * `(deal_id, product_id, project_key)` is UNIQUE. The picker no
           * longer excludes anything — the same product under a different
           * project is a legal line — so this is now a real outcome rather
           * than only a race, and the message names the pair.
           */
          setServerErrors({ productId: caught.message });
        } else {
          /*
           * The inclusions failure reports on `inclusions`, and zod may deepen
           * that to `inclusions.0.quantity`. Those deeper paths ARE routable —
           * the block owns the whole set — so they are declared known and
           * folded onto that one control below.
           */
          const inclusionPaths = Object.keys(caught.fieldErrors).filter(
            (path) => path === 'inclusions' || path.startsWith('inclusions.'),
          );
          /*
           * Split against this form's OWN field list, never the shape of
           * the path. Anything it cannot mark goes to the banner, never
           * counted — see `FormErrorSummary`.
           */
          const { fields, unroutable } = caught.routeFieldErrors([
            ...KNOWN_WRITE_KEYS,
            ...inclusionPaths,
          ]);

          const inclusionMessages: string[] = [];
          for (const path of inclusionPaths) {
            const message = fields[path];
            if (message === undefined) continue;
            inclusionMessages.push(message);
            delete fields[path];
          }
          if (inclusionMessages.length > 0) fields.inclusions = inclusionMessages.join(' ');

          setServerErrors(fields);
          /*
           * `unroutable` is every message no input here can carry: the
           * EMPTY-path `.strict()` and "at least one field" refusals, and any
           * path naming a key with no control on this form. Rendering it at
           * form level is the only way it is seen at all.
           */
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  const selectedCatalogPrice = products.priceById.get(Number(values.productId)) ?? null;

  /* ── Project pricing (owner 2026-10-27) ─────────────────────────────────── */

  const savedProjectId = line?.project?.id ?? null;
  const pricedProjectIds = useMemo(
    () => [projectIdNum, savedProjectId].filter((id): id is number => id !== null),
    [projectIdNum, savedProjectId],
  );
  const prices = useProjectPrices(pricedProjectIds);

  /**
   * `computeLinePrice` for one selection, or `undefined` while something it
   * reads is unread (the catalogue price, the project's city count, or its price
   * entries and tiers). `legacyOnly` asks the catalogue rule alone.
   */
  function priceOf(
    selection: {
      productId: number | null;
      projectId: number | null;
      priceTierId: number | null;
      selectedCityCount: number;
      quantity: number;
    },
    legacyOnly = false,
  ): ComputedLinePrice | undefined {
    if (selection.productId === null) return undefined;
    const catalog = products.priceById.get(selection.productId);
    if (catalog === undefined) return undefined;
    const project =
      selection.projectId === null
        ? undefined
        : lineProject.projects.find((candidate) => candidate.id === selection.projectId);
    const projectCityCount =
      selection.projectId === null
        ? 0
        : (project?.cities.length ?? (selection.selectedCityCount === 0 ? 0 : undefined));
    if (projectCityCount === undefined) return undefined;
    let entries: readonly ProjectPriceEntry[] = [];
    const failed = prices.error !== null;
    if (!legacyOnly && selection.projectId !== null && currency !== null && currency.trim() !== '') {
      const loaded = prices.entriesByProject.get(selection.projectId);
      if (loaded !== undefined) entries = loaded;
      else if (!failed) return undefined;
      if (prices.defaultTierId === null && !failed) return undefined;
    }
    return computeLinePrice({
      entries,
      defaultTierId: prices.defaultTierId,
      productId: selection.productId,
      currency,
      priceTierId: selection.priceTierId,
      selectedCityCount: selection.selectedCityCount,
      projectCityCount,
      quantity: selection.quantity,
      catalogUnitPrice: catalog,
    });
  }

  const quantityText = values.quantity.trim();
  const computedPrice = priceOf({
    productId: productIdNum,
    projectId: projectIdNum,
    priceTierId: effectivePriceTierId,
    selectedCityCount: cityIds.length,
    quantity: /^\d+$/.test(quantityText) ? Number(quantityText) : 1,
  });

  /**
   * The stored price is still what the rule gives the line's OWN saved
   * selection — the project price, or the legacy catalogue pro-rata it was
   * written under — so nobody typed over it and it may follow the line.
   */
  const savedSelection =
    line === undefined
      ? null
      : {
          productId: line.product.id,
          projectId: savedProjectId,
          priceTierId: baselinePriceTierId,
          selectedCityCount: line.cities.length,
          quantity: line.quantity,
        };
  const savedExpected = savedSelection === null ? undefined : priceOf(savedSelection)?.unitPrice;
  const savedLegacy = savedSelection === null ? undefined : priceOf(savedSelection, true)?.unitPrice;
  const savedFollows =
    line !== undefined &&
    line.unitPrice !== null &&
    ((typeof savedExpected === 'string' && sameDecimal(savedExpected, line.unitPrice)) ||
      (typeof savedLegacy === 'string' && sameDecimal(savedLegacy, line.unitPrice)));

  /*
   * ⚠️ **Typed prices are never overwritten.** On a create a blank box is
   * untouched (it omits the key, so the server snapshots); on an edit a blank
   * box is an explicit "no price", so only the placed figure — or a stored one
   * that still follows — is untouched there.
   */
  const unitPriceText = values.unitPrice.trim();
  const priceUntouched =
    (placedPrice !== null && sameDecimal(unitPriceText, placedPrice)) ||
    (line === undefined
      ? unitPriceText === ''
      : placedPrice === null &&
        line.unitPrice !== null &&
        savedFollows &&
        sameDecimal(unitPriceText, line.unitPrice));
  /*
   * What an untouched box should hold. A create places a PROJECT price and
   * leaves the catalogue rule to the server (blank box); an edit places either.
   * `undefined` = change nothing.
   */
  const priceTarget =
    !priceUntouched || computedPrice === undefined
      ? undefined
      : line === undefined
        ? computedPrice.source === 'project_price'
          ? (computedPrice.unitPrice ?? undefined)
          : ''
        : (computedPrice.unitPrice ?? undefined);
  useEffect(() => {
    if (priceTarget === undefined) return;
    if (priceTarget === '' ? unitPriceText === '' : sameDecimal(unitPriceText, priceTarget)) return;
    setValues((prev) => ({ ...prev, unitPrice: priceTarget }));
    setPlacedPrice(priceTarget === '' ? null : priceTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to the target only
  }, [priceTarget]);

  /*
   * The resolver's rounding discount (a quantity override that is not whole
   * cents) goes in the amount box only while the discount is untouched: no
   * discount at all, or the one this form placed.
   */
  const discountUntouched =
    (discountAmount === null && isBlankOrZero(values.discount)) ||
    (placedDiscount !== null && discountAmount !== null && sameDecimal(discountAmount, placedDiscount));
  const discountTarget =
    !priceUntouched ||
    computedPrice === undefined ||
    !discountUntouched ||
    // An edit opens exactly as stored; only a repriced line moves its discount.
    (line !== undefined && placedPrice === null)
      ? undefined
      : computedPrice.source === 'project_price'
        ? computedPrice.discountAmount
        : null;
  useEffect(() => {
    if (discountTarget === undefined) return;
    if (discountTarget === null) {
      if (placedDiscount === null) return;
      setDiscountAmount(null);
      setPlacedDiscount(null);
      return;
    }
    if (discountAmount !== null && sameDecimal(discountAmount, discountTarget)) return;
    setDiscountAmount(discountTarget);
    setPlacedDiscount(discountTarget);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to the target only
  }, [discountTarget]);

  /** Priced tiers plus the default, with the tier the line holds pinned back in. */
  const tierOptions = useMemo(() => {
    const entries =
      projectIdNum === null ? [] : (prices.entriesByProject.get(projectIdNum) ?? []);
    const options = tierOptionsFor(entries, prices.tiers, productIdNum, currency);
    if (priceTierId === null || options.some((tier) => tier.id === priceTierId)) return options;
    const pinned = prices.tiers.find((tier) => tier.id === priceTierId);
    return pinned === undefined ? options : [...options, pinned];
  }, [prices.entriesByProject, prices.tiers, projectIdNum, productIdNum, currency, priceTierId]);
  const tierValue = priceTierId ?? prices.defaultTierId;

  const fieldErrorCount = Object.keys(errors).length;
  const saveDisabled = isEdit ? !isDirty : values.productId === '';

  const offendingCityIds = refusal?.offendingCityIds ?? [];
  const offendingSet = new Set(offendingCityIds);
  /** A stable dependency for the option list — the array identity is not. */
  const offendingKey = offendingCityIds.join(',');

  /**
   * The refused ids as the strings `MultiCombobox` selects by, so every
   * wrong chip is marked where the user will act on it.
   *
   * Rebuilt from `offendingKey` for a stable dependency; the conversion
   * sits here because only this control speaks strings.
   *
   * ⚠️ Does NOT replace the block below. `offendingCityIds` is legitimately
   * `[]` on a real refusal — the parser is all-or-nothing — and the block
   * carries both the server's own wording and the "Remove them" action.
   */
  const offendingCityValues = useMemo(
    () => (offendingKey === '' ? [] : offendingKey.split(',')),
    [offendingKey],
  );

  const projectOptionList = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = offeredProjects.map((project) => ({
      value: String(project.id),
      label: project.name,
      hint: [project.cycle?.name, project.intake?.name].filter(Boolean).join(' · ') || undefined,
    }));
    /*
     * The stored value, injected when the narrowed list does not contain
     * it — a retired project still reads back on the line, and a blank
     * project box would be silently reassigned by the next save. The hook
     * never injects this itself; it answers "what may be CHOSEN".
     *
     * ⚠️ The injection is unconditional; only the REASON attached is gated.
     * An unreadable list gets the bare fact and no explanation — saying
     * less is better than saying something wrong.
     */
    if (line?.project && !list.some((option) => option.value === String(line.project?.id))) {
      list.unshift({
        value: String(line.project.id),
        label: line.project.name,
        hint: projectListReadable
          ? 'Currently on this line — no longer offered'
          : 'Currently on this line',
      });
    }
    return list;
  }, [offeredProjects, line, projectListReadable]);

  const cityOptions = useMemo<MultiComboboxOption[]>(() => {
    // Rebuilt from the key rather than closed over, so the memo has one honest
    // dependency instead of an array whose identity changes every render.
    const wrong = new Set(
      offendingKey === '' ? [] : offendingKey.split(',').map((value) => Number(value)),
    );
    const list: MultiComboboxOption[] = cityChoices.map((city) => ({
      value: String(city.id),
      label: city.name,
      hint: wrong.has(city.id)
        ? 'Not one of this project’s cities'
        : (city.country?.name ?? 'No country recorded'),
    }));
    /*
     * A city the line already holds but the project no longer declares
     * stays visible and marked — reads carry no `isActive` filter, and only
     * RE-SENDING it is refused; an untouched `cityIds` is never sent.
     */
    for (const held of line?.cities ?? []) {
      if (list.some((option) => option.value === String(held.id))) continue;
      list.push({
        value: String(held.id),
        label: held.name,
        // Only claim the project dropped it when the project was actually read.
        // When it was not, all that is known is that the line holds the city.
        hint: projectUnresolved ? 'Already on this line' : 'No longer one of this project’s cities',
      });
    }
    return list;
  }, [cityChoices, line, offendingKey, projectUnresolved]);

  const intakeApplied = narrowing.intakeId !== '';
  const noMatches = !lineProject.loading && lineProject.projects.length === 0;
  const widerCount = lineProject.matchesWithoutIntake;

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissOnBackdrop={false}
      title={line ? `Edit ${line.product.productName}` : 'Add a product'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            {isEdit ? 'Save changes' : 'Add to deal'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-3">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        {refusal !== null && (
          // The summary sentence names the project as well as the cities, which
          // the field-level detail does not. Both are the server's own words.
          <Notice tone="danger">{refusal.summary}</Notice>
        )}

        <Field label="Product" required error={errors.productId}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={values.productId}
              onChange={chooseProduct}
              options={products.options}
              onSearch={products.setTerm}
              loading={products.loading}
              placeholder="Choose a product"
              searchPlaceholder="Search products…"
              invalid={Boolean(errors.productId)}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        {productIdNum !== null && projectless && (
          /*
           * Not a disabled control and not an empty dropdown — this product
           * isn't sold through projects at all, so "choose a project first"
           * is advice nobody can follow.
           *
           * ⚠️ **It DOES name regions**, which is why this is no longer a bare
           * paragraph. A project declares its own regions, so a project-backed
           * line has somewhere to derive them from; a projectless line had
           * nowhere at all to record which markets it belongs to.
           *
           * ⚠️ **The "Region" combobox inside `ProjectPicker` is NOT this
           * control.** That one narrows the project search and is never sent;
           * this one is saved on the line. Same master, opposite jobs — do not
           * deduplicate them, and do not share their state.
           */
          <div className="space-y-3 rounded-md border border-hairline bg-surface-sunken px-3 py-3">
            <p className="text-xs text-fg-muted">
              Not sold through projects — no project or cities on this line.
            </p>

            <Field
              label="Regions of focus on this line"
              error={errors.regionIds}
            >
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={regionIds}
                  onChange={(next) => {
                    setRegionIds(next);
                    setClientErrors((prev) => without(prev, 'regionIds'));
                    setServerErrors((prev) => without(prev, 'regionIds'));
                    setRefusal(null);
                  }}
                  options={lineRegionOptions}
                  placeholder="No regions"
                  searchPlaceholder="Filter regions…"
                  loading={regions.loading && regions.records.length === 0}
                  // Form-staged: reviewed before Save. ⚠️ A projectless line
                  // takes its Xero tracking Location from this set, and ONE
                  // region is a Location where TWO OR MORE push with NO
                  // Location, silently — so a select-all can drop it. Kept
                  // because this is a form; the INCLUSIONS picker below is
                  // deliberately excluded instead, since that set prints on
                  // the customer's document.
                  selectAll
                  invalid={Boolean(errors.regionIds)}
                />
              )}
            </Field>
          </div>
        )}

        {productIdNum !== null && !projectless && (
          <ProjectPicker
            values={values}
            narrowing={narrowing}
            setNarrowing={setNarrowing}
            onChooseProject={chooseProject}
            options={projectOptionList}
            scope={projectScope}
            onScopeChange={setProjectScope}
            scopeFootnote={projectScopeFootnote(projectScope, {
              current: projectsByScope.current.length,
              previous: projectsByScope.previous.length,
            })}
            state={lineProject}
            regions={regions}
            cycles={cycles}
            intakes={intakes}
            error={errors.projectId}
            noMatches={noMatches}
            intakeApplied={intakeApplied}
            widerCount={widerCount}
          />
        )}

        {productIdNum !== null && !projectless && projectIdNum !== null && prices.tiers.length > 0 && (
          /*
           * The line's price tier, directly under the project (owner
           * 2026-10-27). Choosing the default writes `null`; tiers are matched by
           * id and the default by its flag, never by name.
           */
          <Field label="Price" error={errors.priceTierId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={tierValue === null ? '' : String(tierValue)}
                onChange={(event) => {
                  const next = Number(event.target.value);
                  setPriceTierId(next === prices.defaultTierId ? null : next);
                  setClientErrors((prev) => without(prev, 'priceTierId'));
                  setServerErrors((prev) => without(prev, 'priceTierId'));
                  setRefusal(null);
                }}
                invalid={Boolean(errors.priceTierId)}
              >
                {tierOptions.map((tier) => (
                  <option key={tier.id} value={String(tier.id)}>
                    {tier.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {productIdNum !== null && !projectless && (
          <Field
            label="Cities"
            error={errors.cityIds}
            hint={
              projectIdNum === null
                ? 'Choose a project first.'
                : projectUnresolved
                  ? 'This project is not in the list above — the cities on the line are shown as stored.'
                  : cityChoices.length === 0
                    ? 'This project declares no cities.'
                    : undefined
            }
          >
            {({ id, 'aria-describedby': describedBy }) => (
              <div className="space-y-2">
                <MultiCombobox
                  id={id}
                  values={cityIds}
                  onChange={(next) => {
                    setCityIds(next);
                    setDroppedCities([]);
                    setClientErrors((prev) => without(prev, 'cityIds'));
                    setServerErrors((prev) => without(prev, 'cityIds'));
                    setRefusal(null);
                  }}
                  options={cityOptions}
                  loading={lineProject.loading}
                  /*
                   * Disabled until a project resolves, which is what makes
                   * "cities named on a line with no project" unreachable rather
                   * than merely explained afterwards.
                   */
                  disabled={projectIdNum === null}
                  placeholder={projectIdNum === null ? 'No project yet' : 'No cities'}
                  searchPlaceholder="Search cities…"
                  maxSelected={LINE_CITIES_MAX}
                  // Form-staged: reviewed before Save, and the options are
                  // already scoped to the line's own project, so "all" is the
                  // cities that project runs in.
                  selectAll
                  invalid={Boolean(errors.cityIds)}
                  // The 422 names every offending id on purpose, so mark every
                  // wrong chip on purpose. Empty on every other refusal and
                  // empty when the sentence could not be parsed with certainty
                  // — `invalid` above and the block below carry it either way.
                  invalidValues={offendingCityValues}
                  aria-describedby={describedBy}
                />

                {offendingCityIds.length > 0 && (
                  <Notice tone="danger">
                    <p>Not on this project: {offendingCityIds.map(cityNameOf).join(', ')}</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="mt-1.5"
                      onClick={() => {
                        setCityIds((prev) =>
                          prev.filter((value) => !offendingSet.has(Number(value))),
                        );
                        setRefusal(null);
                        setServerErrors((prev) => without(prev, 'cityIds'));
                      }}
                    >
                      Remove {offendingCityIds.length === 1 ? 'it' : 'them'}
                    </Button>
                  </Notice>
                )}

                {droppedCities.length > 0 && (
                  <p className="text-xs text-warning">
                    Removed — the new project does not run in {droppedCities.join(', ')}.
                  </p>
                )}
              </div>
            )}
          </Field>
        )}

        <div className="grid min-w-0 gap-3 sm:grid-cols-2">
          <Field label="Quantity" required error={errors.quantity}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                inputMode="numeric"
                value={values.quantity}
                min={selectedStep ?? 1}
                step={selectedStep ?? 1}
                placeholder="1"
                invalid={Boolean(errors.quantity)}
                aria-describedby={describedBy}
                onChange={(event) => changeQuantity(event.target.value)}
              />
            )}
          </Field>

          {/*
            ⚠️ **ONE control with a MODE, never two boxes.** `discount` and
            `discountAmount` are mutually exclusive columns, so two live boxes
            would let somebody fill both and collect a refusal they cannot read.
            The switch decides which one is on screen; `discountWriteOf` turns
            that into the pair the wire carries.

            The two labels come from `@/lib/discount-mode`, which is the one copy
            of this wording — the owner's instruction is that the grids and this
            form must not word the same choice differently. ⛔ Never label the
            amount with a currency code.
          */}
          <div className="space-y-1.5">
            <SegmentedControl
              label={DISCOUNT_MODE_GROUP_LABEL}
              value={discountMode}
              onChange={chooseDiscountMode}
              options={DISCOUNT_MODE_OPTIONS}
            />
            {discountMode === 'percentage' ? (
              <Field label="Discount %" error={errors.discount}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={values.discount}
                    placeholder="0"
                    invalid={Boolean(errors.discount)}
                    aria-describedby={describedBy}
                    onChange={(event) => setField('discount', event.target.value)}
                  />
                )}
              </Field>
            ) : (
              <Field label="Discount amount" error={errors.discountAmount}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    inputMode="decimal"
                    value={discountAmount ?? ''}
                    placeholder="0.00"
                    invalid={Boolean(errors.discountAmount)}
                    aria-describedby={describedBy}
                    onChange={(event) => setDiscountAmountValue(event.target.value)}
                  />
                )}
              </Field>
            )}
          </div>

          <div className="sm:col-span-2">
            {/*
              ⚠️ The blank box still means OPPOSITE things by mode — blank on
              CREATE omits the key and snapshots the catalogue price; clearing
              on EDIT sends an explicit `null` and records NO PRICE AT ALL. The
              sentence that said so was removed on the owner's stricter
              no-helper-text ruling (2026-09-05); the behaviour is unchanged.
            */}
            <Field label="Unit price" error={errors.unitPrice}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={values.unitPrice}
                  placeholder={selectedCatalogPrice ?? undefined}
                  invalid={Boolean(errors.unitPrice)}
                  aria-describedby={describedBy}
                  onChange={(event) => setField('unitPrice', event.target.value)}
                />
              )}
            </Field>
            {selectedUnitLabel !== null && (
              <span className="mt-1 block text-2xs text-fg-muted">per {selectedUnitLabel}</span>
            )}
          </div>

          <Field label="Sort order" error={errors.sortOrder}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                inputMode="numeric"
                value={values.sortOrder}
                placeholder="0"
                invalid={Boolean(errors.sortOrder)}
                aria-describedby={describedBy}
                onChange={(event) => setField('sortOrder', event.target.value)}
              />
            )}
          </Field>
        </div>

        <InclusionsField
          error={errors.inclusions}
          selected={selected}
          options={inclusions.options}
          loading={inclusions.loading}
          failed={inclusions.failed}
          labelFor={inclusions.labelFor}
          renderFor={inclusions.renderFor}
          isRetired={inclusions.isRetired}
          onChangeIds={setInclusionIds}
          onChangeQuantity={setInclusionQuantity}
        />

        {/*
          ⚠️ `422 INCLUSION_NOT_OFFERED_IN_REGION` — migration 096, and the only
          refusal in the line guard with THREE destinations, which is why it
          carries a code at all.

          The server's own sentence renders ABOVE this, on the control, through
          `errors.inclusions`; it names the inclusion, the region and the product
          and nothing here repeats it. This says only where to go — and two of
          the three places are not on this line, so naming only the line would
          send somebody to a picker that will never list what they came for.
        */}
        {refusal?.kind === 'inclusion-not-offered-in-region' && (
          <Notice tone="danger">
            <ul className="grid list-disc gap-0.5 pl-4">
              <li>Choose another inclusion, or a project in a region that offers it.</li>
              <li>Widen it on the Products screen (administrator).</li>
              <li>Add the region to this line’s project.</li>
            </ul>
          </Notice>
        )}
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * The Current / Previous pills, inside the project dropdown.
 *
 * ⚠️ **Current means current AND UPCOMING** — the owner's own clarification, and
 * the reason the rule is one `endDate >= today` comparison rather than two. See
 * `src/lib/project-cycle-scope.ts`.
 *
 * ⛔ **No hint, no sentence, no tooltip. The two labels are the whole
 * explanation.** The only prose the popover may carry is the `footnote`, which
 * states a fact about the data.
 *
 * `SegmentedControl` rather than hand-rolled pills: it already gives the group
 * label, `aria-pressed` per segment, a tick so selection is not colour alone,
 * and a constant width across selections.
 */
function ProjectScopePills({
  value,
  onChange,
}: {
  value: ProjectCycleScope;
  onChange: (next: ProjectCycleScope) => void;
}) {
  return (
    <SegmentedControl
      label="Show projects from"
      value={value}
      onChange={onChange}
      options={PROJECT_CYCLE_SCOPES.map((scope) => ({
        value: scope,
        label: PROJECT_CYCLE_SCOPE_LABELS[scope],
      }))}
    />
  );
}

/**
 * The project control and the three boxes that narrow it.
 *
 * ⚠️ The intake narrows LAST and is optional on a project: a project MUST
 * have a cycle but MAY have no intake, spanning the whole cycle — and
 * `?intakeId=` does not match a null column. So an intake filter silently
 * drops every project that spans the chosen window via its cycle, and "no
 * project matches" reads true of the filter and false of the world. Hence
 * the counterfactual — *"no project matches with Fall 2026 selected; 2
 * match without it"* — and a control to drop it, rather than inviting a
 * duplicate project to be created.
 */
function ProjectPicker({
  values,
  narrowing,
  setNarrowing,
  onChooseProject,
  options,
  state,
  regions,
  cycles,
  intakes,
  error,
  noMatches,
  intakeApplied,
  widerCount,
  scope,
  onScopeChange,
  scopeFootnote,
}: {
  values: FormValues;
  narrowing: Narrowing;
  setNarrowing: (next: Narrowing) => void;
  onChooseProject: (next: string) => void;
  /** Already narrowed to `scope`, with the line's own project injected. */
  options: ComboboxOption[];
  /** The Current / Previous pill, owned by the form so the options match it. */
  scope: ProjectCycleScope;
  onScopeChange: (next: ProjectCycleScope) => void;
  /** "No projects in Current. 5 in Previous." — see `projectScopeFootnote`. */
  scopeFootnote: string | undefined;
  state: ReturnType<typeof useLineProject>;
  regions: ReturnType<typeof useReferenceOptions>;
  cycles: ReturnType<typeof useCycleOptions>;
  intakes: { records: Intake[]; loading: boolean };
  error?: string;
  noMatches: boolean;
  intakeApplied: boolean;
  widerCount: number | null;
}) {
  const regionOptions = useMemo<ComboboxOption[]>(
    () => regions.records.map((row) => ({ value: String(row.id), label: row.name })),
    [regions.records],
  );
  const cycleOptions = useMemo<ComboboxOption[]>(
    () => cycles.cycles.map((row) => ({ value: String(row.id), label: row.cycleName })),
    [cycles.cycles],
  );
  const intakeOptions = useMemo<ComboboxOption[]>(
    () => intakes.records.map((row) => ({ value: String(row.id), label: row.intakeName })),
    [intakes.records],
  );

  const intakeName =
    intakes.records.find((row) => String(row.id) === narrowing.intakeId)?.intakeName ??
    'that intake';

  return (
    <div className="space-y-3 rounded-md border border-hairline bg-surface-sunken px-3 py-3">
      <div className="grid min-w-0 gap-3 sm:grid-cols-3">
        {/* ⚠️ A SEARCH BOX, not a field of the line. Nothing here is sent. The
            line's OWN regions are the multiselect in the `projectless` branch,
            which is saved — same master, opposite jobs. Do not merge them. */}
        <Field label="Region (filter)">
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={narrowing.regionId}
              onChange={(next) => setNarrowing({ ...narrowing, regionId: next })}
              options={regionOptions}
              placeholder="Any region"
              emptyOptionLabel="Any region"
              searchPlaceholder="Filter regions…"
              loading={regions.loading && regionOptions.length === 0}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Cycle">
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={narrowing.cycleId}
              onChange={(next) =>
                // The intake belongs to the cycle, so it cannot survive one
                // changing — the same reset the project form itself performs.
                setNarrowing({ ...narrowing, cycleId: next, intakeId: '' })
              }
              options={cycleOptions}
              placeholder="Any cycle"
              emptyOptionLabel="Any cycle"
              searchPlaceholder="Filter cycles…"
              loading={cycles.loading && cycleOptions.length === 0}
              aria-describedby={describedBy}
            />
          )}
        </Field>

        <Field label="Intake">
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={narrowing.intakeId}
              onChange={(next) => setNarrowing({ ...narrowing, intakeId: next })}
              options={intakeOptions}
              placeholder="Any intake"
              emptyOptionLabel="Any intake"
              searchPlaceholder="Filter intakes…"
              loading={intakes.loading && intakeOptions.length === 0}
              disabled={narrowing.cycleId === ''}
              aria-describedby={describedBy}
            />
          )}
        </Field>
      </div>

      <Field label="Project" error={error}>
        {({ id, 'aria-describedby': describedBy }) => (
          <Combobox
            id={id}
            value={values.projectId}
            onChange={onChooseProject}
            options={options}
            placeholder="No project"
            emptyOptionLabel="No project"
            searchPlaceholder="Search projects…"
            header={<ProjectScopePills value={scope} onChange={onScopeChange} />}
            {...(scopeFootnote ? { footnote: scopeFootnote } : {})}
            loading={state.loading && options.length === 0}
            invalid={Boolean(error)}
            aria-describedby={describedBy}
          />
        )}
      </Field>

      {state.error !== null && <p className="text-xs text-danger">{state.error}</p>}

      {state.error === null && noMatches && intakeApplied && widerCount !== null && widerCount > 0 && (
        <Notice tone="warning">
          <p>
            No project matches with {intakeName} selected; {widerCount}{' '}
            {widerCount === 1 ? 'matches' : 'match'} without it.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-1.5"
            onClick={() => setNarrowing({ ...narrowing, intakeId: '' })}
          >
            Search without the intake
          </Button>
        </Notice>
      )}

      {state.error === null && noMatches && !(intakeApplied && widerCount !== null && widerCount > 0) && (
        <p className="text-xs text-fg-muted">
          {values.projectId === ''
            ? 'No project matches. The line can be saved with none, and will name no cities.'
            : 'No project matches these filters. The one on this line is kept.'}
        </p>
      )}
    </div>
  );
}

/**
 * The campaign inclusions block: which inclusions, and how many of each.
 *
 * The quantity sits beside the name rather than inside the picker because it
 * belongs to the line, not to the master — the master owns the vocabulary and
 * the two unit forms, the line owns the count.
 */
function InclusionsField({
  error,
  selected,
  options,
  loading,
  failed,
  footnote,
  labelFor,
  renderFor,
  isRetired,
  onChangeIds,
  onChangeQuantity,
}: {
  error?: string;
  selected: SelectedInclusion[];
  options: MultiComboboxOption[];
  loading: boolean;
  failed: boolean;
  footnote?: string;
  labelFor: (id: number) => string;
  renderFor: (id: number, quantity: number) => string | null;
  isRetired: (id: number) => boolean;
  onChangeIds: (ids: string[]) => void;
  onChangeQuantity: (id: number, quantity: string) => void;
}) {
  const ids = selected.map((entry) => String(entry.inclusionId));

  return (
    <Field
      label="Campaign inclusions"
      error={error}
      // Replace semantics survive the sweep: `[]` clears the line's whole set.
      hint="Saving replaces the whole set."
    >
      {({ id, 'aria-describedby': describedBy }) => (
        <div className="space-y-2">
          <MultiCombobox
            id={id}
            values={ids}
            onChange={onChangeIds}
            options={options}
            loading={loading}
            disabled={failed && options.length === 0}
            placeholder="No inclusions"
            searchPlaceholder="Search inclusions…"
            maxSelected={INCLUSIONS_MAX}
            /*
             * A product offering five inclusions and a line taking all five is
             * the ordinary case — product 1 is the only product offering any,
             * and all five are `required: false`, so ticking them one at a time
             * is the whole of the work here.
             *
             * Every newly ticked entry lands at quantity 1 through
             * `onChangeIds`; the row toggles MEMBERSHIP and never a count, so
             * the quantity list below is unaffected by it.
             */
            selectAll
            {...(footnote ? { footnote } : {})}
            invalid={Boolean(error)}
            aria-describedby={describedBy}
          />

          {selected.length > 0 && (
            <ul className="space-y-1.5 rounded-md border border-hairline bg-surface-sunken px-3 py-2">
              {selected.map((entry) => {
                const raw = entry.quantity.trim();
                const quantity = raw === '' ? 1 : /^\d+$/.test(raw) ? Number(raw) : null;
                const label = labelFor(entry.inclusionId);
                /*
                 * Shown exactly as it will print. The unit is CHOSEN from
                 * the two stored forms, never derived, and an inclusion
                 * recording no unit prints a bare name with NO number.
                 * `@/lib/inclusions` owns this for every surface.
                 */
                const printed = quantity === null ? label : renderFor(entry.inclusionId, quantity);
                return (
                  <li key={entry.inclusionId} className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs text-fg">
                        {printed ?? label}
                        {isRetired(entry.inclusionId) && (
                          <span className="text-warning"> · no longer available</span>
                        )}
                      </p>
                    </div>
                    <Input
                      className="w-20 shrink-0"
                      inputMode="numeric"
                      value={entry.quantity}
                      placeholder="1"
                      aria-label={`Quantity for ${label}`}
                      invalid={quantity === null || quantity < 1}
                      onChange={(event) => onChangeQuantity(entry.inclusionId, event.target.value)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={() =>
                        onChangeIds(ids.filter((value) => value !== String(entry.inclusionId)))
                      }
                    >
                      Remove
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Field>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Server-side product search for the line picker.
 *
 * ⚠️ Does NOT exclude products already on the deal — `UNIQUE (deal_id,
 * product_id, project_key)` (migration 067) makes the same product under a
 * different project a legal line. What is taken is the (product, project)
 * pair, checked here where the project is known and refused server-side as
 * a 409 either way.
 *
 * The catalogue price is kept alongside to show what a blank price box will
 * snapshot — the line-item read doesn't expose it.
 *
 * ⚠️ **So is the RECOMMENDED DISCOUNT, and it costs no request**: `/products`
 * already serves `recommendedDiscountAmount` on the rows this search reads, so
 * the seed reads a field that was on the wire rather than fetching a product
 * again. ⛔ **`null` is NO RECOMMENDATION and `"0.00"` is a recommendation of
 * nothing off** — the same distinction `unitPrice` draws between unpriced and
 * free, and the same `?? 0` that would destroy it.
 */
function useProductSearch(line: DealProduct | null) {
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState<
    {
      id: number;
      productName: string;
      productCode: string | null;
      unitPrice: string | null;
      recommendedDiscountAmount: string | null;
    }[]
  >([]);
  const [, setTruncated] = useState(false);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  /*
   * Each product's `quantityStep` / `unitLabel`, kept for EVERY product any
   * search returned — a later search narrowing the list must not forget the
   * chosen product's step.
   */
  const [unitsById, setUnitsById] = useState<ReadonlyMap<number, ProductUnitFields>>(
    () => new Map(),
  );

  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts({ search: term, perPage: 100, active: true }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(
          result.data.map((row) => ({
            id: row.id,
            productName: row.productName,
            productCode: row.productCode,
            unitPrice: row.unitPrice,
            recommendedDiscountAmount: row.recommendedDiscountAmount,
          })),
        );
        setUnitsById((prev) => {
          const next = new Map(prev);
          for (const row of result.data) {
            next.set(row.id, {
              ...(row.quantityStep !== undefined ? { quantityStep: row.quantityStep } : {}),
              ...(row.unitLabel !== undefined ? { unitLabel: row.unitLabel } : {}),
            });
          }
          return next;
        });
        setTruncated(result.meta.totalPages > 1);
      })
      .catch(() => {
        if (!controller.signal.aborted) setRows([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadedTerm(term);
      });
    return () => controller.abort();
  }, [term]);

  const options = useMemo<ComboboxOption[]>(() => {
    const list = rows.map((row) => ({
      value: String(row.id),
      label: row.productName,
      ...(row.productCode ? { hint: row.productCode } : {}),
    }));
    // The line's own product is pinned back in when it is not in the current
    // page of results, so the field is never left looking empty.
    if (line && !list.some((option) => option.value === String(line.product.id))) {
      list.unshift({
        value: String(line.product.id),
        label: line.product.productName,
        ...(line.product.productCode ? { hint: line.product.productCode } : {}),
      });
    }
    return list;
  }, [rows, line]);

  const priceById = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const row of rows) map.set(row.id, row.unitPrice);
    return map;
  }, [rows]);

  /**
   * ⚠️ **Built from `rows` ALONE, so a product the search has not returned is
   * ABSENT rather than `null`-by-default — and both read as "no
   * recommendation".** That is the safe direction: the line's own product is
   * pinned into the options without a catalogue row behind it, and seeding from
   * a list that never arrived would put a figure on a customer's line on the
   * strength of nothing.
   */
  const recommendedById = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const row of rows) map.set(row.id, row.recommendedDiscountAmount);
    return map;
  }, [rows]);

  return {
    options,
    priceById,
    recommendedById,
    unitsById,
    setTerm,
    loading: loadedTerm !== term,
  };
}

/**
 * The intakes of one cycle, for the project picker's last narrowing step.
 *
 * Scoped to a cycle rather than listed whole: an intake always belongs to one,
 * a project's intake must belong to its cycle, and an unscoped list would offer
 * windows from years the person is not looking at.
 */
function useIntakeOptions(cycleId: number | null) {
  const [records, setRecords] = useState<Intake[]>([]);
  const [loading, setLoading] = useState(false);

  /*
   * Dropped DURING RENDER rather than after the fetch resolves — React's
   * adjust-state-when-props-change pattern, same as `useReferenceOptions`.
   * Doing it in an effect would paint one frame offering the old cycle's
   * windows.
   */
  const [loadedCycleId, setLoadedCycleId] = useState<number | null>(cycleId);
  if (loadedCycleId !== cycleId) {
    setLoadedCycleId(cycleId);
    setRecords([]);
    setLoading(cycleId !== null);
  }

  useEffect(() => {
    if (cycleId === null) return;
    const controller = new AbortController();
    api
      .listIntakes({ page: 1, perPage: 100, cycleId }, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setRecords(result.data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setRecords([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [cycleId]);

  return { records, loading };
}

/**
 * The campaign inclusions master, plus whatever the line already holds.
 *
 * The picker offers `?isActive=true` only — re-sending a retired id is a 422 —
 * while a line's stored inclusions stay visible and are marked, because reads
 * carry no active filter and a document must not lose what it promised.
 */
function useCampaignInclusions(line: DealProduct | undefined) {
  const [records, setRecords] = useState<CampaignInclusion[]>([]);
  const [, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const held = useMemo(() => (line === undefined ? [] : line.inclusions), [line]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCampaignInclusions({ isActive: true, perPage: 200 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRecords(result.data);
        setTruncated(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setRecords([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const byId = useMemo(() => {
    const map = new Map<
      number,
      { name: string; unitSingular: string | null; unitPlural: string | null; retired: boolean }
    >();
    for (const record of records) {
      map.set(record.id, {
        name: record.name,
        unitSingular: record.unitSingular,
        unitPlural: record.unitPlural,
        retired: false,
      });
    }
    // Absence from a list that never arrived is not evidence of anything.
    const listIsTrustworthy = !failed && !loading;
    for (const entry of held) {
      if (map.has(entry.id)) continue;
      map.set(entry.id, {
        name: entry.name,
        unitSingular: entry.unitSingular,
        unitPlural: entry.unitPlural,
        retired: listIsTrustworthy,
      });
    }
    return map;
  }, [records, held, failed, loading]);

  const options = useMemo<MultiComboboxOption[]>(() => {
    const list: MultiComboboxOption[] = records.map((record) => ({
      value: String(record.id),
      label: record.name,
      ...(record.unitPlural ? { hint: `Counted in ${record.unitPlural}` } : {}),
    }));
    for (const entry of held) {
      if (list.some((option) => option.value === String(entry.id))) continue;
      list.push({ value: String(entry.id), label: entry.name, hint: 'No longer available' });
    }
    return list;
  }, [records, held]);

  return {
    options,
    loading,
    failed,
    labelFor: (id: number) => byId.get(id)?.name ?? `Inclusion ${id}`,
    isRetired: (id: number) => byId.get(id)?.retired ?? false,
    /**
     * Exactly how the inclusion will print — `Campus Outreach: 5 campuses`,
     * or a bare name with no number when the master records no unit.
     * `@/lib/inclusions` owns the choice; nothing here pluralises.
     *
     * Null when the inclusion is unknown to this form.
     */
    renderFor: (id: number, quantity: number) => {
      const found = byId.get(id);
      if (!found) return null;
      return renderInclusion(found, quantity);
    },
  };
}
